import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Package, 
    Truck, 
    DollarSign, 
    Users, 
    TrendingUp, 
    Clock,
    ArrowUpRight,
    ArrowDownRight,
    MoreHorizontal,
    ExternalLink,
    Wallet,
    PiggyBank,
    Receipt,
    AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../components/ui/tooltip';
import TruckPerformancePanel from '../components/TruckPerformancePanel';
import { apiFetch } from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardPage() {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [accountingData, setAccountingData] = useState(null);
    const [settings, setSettings] = useState(null);
    const [recentLoads, setRecentLoads] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            const [statsRes, loadsRes, accountingRes, settingsRes] = await Promise.all([
                apiFetch(`${API_URL}/api/dashboard/stats`, { credentials: 'include' }),
                apiFetch(`${API_URL}/api/dashboard/recent-loads?limit=5`, { credentials: 'include' }),
                apiFetch(`${API_URL}/api/accounting/dashboard`, { credentials: 'include' }),
                apiFetch(`${API_URL}/api/settings`, { credentials: 'include' })
            ]);

            if (statsRes.ok) {
                const statsData = await statsRes.json();
                setStats(statsData);
            }

            if (loadsRes.ok) {
                const loadsData = await loadsRes.json();
                setRecentLoads(loadsData);
            }

            if (accountingRes.ok) {
                const accountingDataRes = await accountingRes.json();
                setAccountingData(accountingDataRes);
            }

            if (settingsRes.ok) {
                const settingsData = await settingsRes.json();
                setSettings(settingsData);
            }
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    const getStatusBadge = (status) => {
        const statusStyles = {
            pending: 'status-pending',
            booked: 'status-booked',
            in_transit: 'status-in-transit',
            delivered: 'status-delivered',
            cancelled: 'status-delayed'
        };
        return statusStyles[status] || 'status-pending';
    };

    // Get dynamic colors based on values
    const getMonthProfitColor = () => {
        const profit = accountingData?.month_to_date?.profit || 0;
        if (profit > 0) return 'text-green-500';
        if (profit < 0) return 'text-red-500';
        return 'text-zinc-400';
    };

    const getCurrentBalanceColor = () => {
        const balance = accountingData?.current_balance || 0;
        return balance < 0 ? 'text-red-500' : 'text-white';
    };

    // Tax reserve warning check
    const defaultTaxPercent = settings?.default_tax_percent || 25;
    const expectedTax = (accountingData?.month_to_date?.income || 0) * (defaultTaxPercent / 100);
    const taxReserved = accountingData?.reserves?.total_tax_reserved || 0;
    const showTaxWarning = taxReserved < expectedTax && expectedTax > 0;

    // Savings reserve warning check
    const defaultSavingsPercent = settings?.default_savings_percent || 10;
    const monthProfit = accountingData?.month_to_date?.profit || 0;
    const expectedSavings = monthProfit > 0 ? monthProfit * (defaultSavingsPercent / 100) : 0;
    const savingsReserved = accountingData?.reserves?.total_savings_reserved || 0;
    const showSavingsWarning = savingsReserved < expectedSavings && expectedSavings > 0;

    // Cash runway calculation
    const cashRunwayDays = accountingData?.cash_flow?.cash_runway_days;
    const getCashRunwayColor = () => {
        if (cashRunwayDays === null || cashRunwayDays === undefined) return 'text-zinc-400';
        if (cashRunwayDays < 30) return 'text-red-500';
        if (cashRunwayDays < 60) return 'text-amber-500';
        return 'text-green-500';
    };
    const formatRunwayDays = (days) => {
        if (days === null || days === undefined) return '—';
        if (days > 365) return '365+ days';
        return `${Math.round(days)} days`;
    };

    // Financial KPI cards using real accounting data with dynamic colors
    const financialKpiCards = [
        {
            title: 'Current Balance',
            value: formatCurrency(accountingData?.current_balance),
            valueColor: getCurrentBalanceColor(),
            subtext: cashRunwayDays !== null ? `${formatRunwayDays(cashRunwayDays)} runway` : 'Available funds',
            subtextColor: getCashRunwayColor(),
            icon: Wallet,
            iconColor: 'text-green-500'
        },
        {
            title: 'Month Profit',
            value: formatCurrency(accountingData?.month_to_date?.profit),
            valueColor: getMonthProfitColor(),
            subtext: `${accountingData?.month_to_date?.period || 'MTD'}`,
            icon: TrendingUp,
            iconColor: getMonthProfitColor()
        },
        {
            title: 'Tax Reserved',
            value: formatCurrency(accountingData?.reserves?.total_tax_reserved),
            valueColor: showTaxWarning ? 'text-amber-500' : 'text-white',
            subtext: 'Set aside for taxes',
            icon: Receipt,
            iconColor: 'text-amber-500',
            showWarning: showTaxWarning,
            warningText: 'Tax reserve below expected allocation'
        },
        {
            title: 'Savings Reserved',
            value: formatCurrency(accountingData?.reserves?.total_savings_reserved),
            valueColor: showSavingsWarning ? 'text-amber-400' : 'text-white',
            subtext: 'Emergency fund',
            icon: PiggyBank,
            iconColor: showSavingsWarning ? 'text-amber-400' : 'text-blue-500'
        }
    ];

    // Operational KPI cards
    const operationalKpiCards = [
        {
            title: 'Active Loads',
            value: stats?.loads?.booked + stats?.loads?.in_transit || 0,
            subtext: `${stats?.loads?.in_transit || 0} in transit`,
            icon: Package,
            color: 'text-orange-500'
        },
        {
            title: 'Available Trucks',
            value: stats?.trucks?.available || 0,
            subtext: `${stats?.trucks?.total || 0} total`,
            icon: Truck,
            color: 'text-blue-500'
        }
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
            {/* Page Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white tracking-tight uppercase">Dashboard</h1>
                    <p className="text-zinc-500 text-sm mt-1">Overview of your logistics operations</p>
                </div>
                <div className="flex items-center gap-3">
                    <Button 
                        variant="outline" 
                        className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                        onClick={() => navigate('/loadboard')}
                        data-testid="find-loads-btn"
                    >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Find Loads
                    </Button>
                    <Button 
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                        onClick={() => navigate('/loads')}
                        data-testid="create-load-btn"
                    >
                        + New Load
                    </Button>
                </div>
            </div>

            {/* Financial KPI Cards */}
            <TooltipProvider>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {financialKpiCards.map((kpi, index) => (
                        <Card 
                            key={index} 
                            className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors"
                            data-testid={`kpi-card-${kpi.title.toLowerCase().replace(/ /g, '-')}`}
                        >
                            <CardContent className="p-5">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                                                {kpi.title}
                                            </p>
                                            {kpi.showWarning && (
                                                <Tooltip>
                                                    <TooltipTrigger>
                                                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                                                    </TooltipTrigger>
                                                    <TooltipContent className="bg-zinc-800 border-zinc-700 text-zinc-200">
                                                        <p>{kpi.warningText}</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            )}
                                        </div>
                                        <p className={`text-3xl font-bold font-mono ${kpi.valueColor}`}>
                                            {kpi.value}
                                        </p>
                                        <p className={`text-sm mt-1 ${kpi.subtextColor || 'text-zinc-500'}`}>{kpi.subtext}</p>
                                    </div>
                                    <div className={`p-2 rounded-sm bg-zinc-800 ${kpi.iconColor}`}>
                                        <kpi.icon className="w-5 h-5" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </TooltipProvider>

            {/* Operational KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {operationalKpiCards.map((kpi, index) => (
                    <Card 
                        key={index} 
                        className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors"
                        data-testid={`kpi-card-${kpi.title.toLowerCase().replace(/ /g, '-')}`}
                    >
                        <CardContent className="p-5">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">
                                        {kpi.title}
                                    </p>
                                    <p className="text-3xl font-bold text-white font-mono">
                                        {kpi.value}
                                    </p>
                                    <p className="text-sm text-zinc-500 mt-1">{kpi.subtext}</p>
                                </div>
                                <div className={`p-2 rounded-sm bg-zinc-800 ${kpi.color}`}>
                                    <kpi.icon className="w-5 h-5" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Recent Loads */}
                <Card className="lg:col-span-2 bg-zinc-900/50 border-zinc-800" data-testid="recent-loads-card">
                    <CardHeader className="border-b border-zinc-800 pb-4">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide">
                                Recent Loads
                            </CardTitle>
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-zinc-400 hover:text-white"
                                onClick={() => navigate('/loads')}
                                data-testid="view-all-loads-btn"
                            >
                                View All
                                <ArrowUpRight className="w-4 h-4 ml-1" />
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        {recentLoads.length === 0 ? (
                            <div className="empty-state py-12">
                                <Package className="w-12 h-12 text-zinc-700 mb-4" />
                                <p className="text-zinc-500">No loads yet</p>
                                <Button 
                                    variant="outline" 
                                    className="mt-4 border-zinc-700"
                                    onClick={() => navigate('/loadboard')}
                                >
                                    Search Load Board
                                </Button>
                            </div>
                        ) : (
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Load ID</th>
                                        <th>Route</th>
                                        <th>Equipment</th>
                                        <th>Rate</th>
                                        <th>Status</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentLoads.map((load) => (
                                        <tr 
                                            key={load.load_id} 
                                            className="cursor-pointer"
                                            onClick={() => navigate(`/loads/${load.load_id}`)}
                                            data-testid={`load-row-${load.load_id}`}
                                        >
                                            <td className="text-orange-500">{load.load_id}</td>
                                            <td className="text-white">
                                                {load.origin_city}, {load.origin_state} → {load.destination_city}, {load.destination_state}
                                            </td>
                                            <td className="text-zinc-400">{load.equipment_type}</td>
                                            <td className="text-green-400">{formatCurrency(load.rate)}</td>
                                            <td>
                                                <Badge className={`${getStatusBadge(load.status)} text-xs uppercase`}>
                                                    {load.status?.replace('_', ' ')}
                                                </Badge>
                                            </td>
                                            <td>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon" className="h-8 w-8">
                                                            <MoreHorizontal className="h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                                                        <DropdownMenuItem 
                                                            className="cursor-pointer"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/loads/${load.load_id}`);
                                                            }}
                                                        >
                                                            View Details
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </CardContent>
                </Card>

                {/* Quick Stats Sidebar */}
                <div className="space-y-6">
                    {/* Load Status Summary */}
                    <Card className="bg-zinc-900/50 border-zinc-800" data-testid="load-status-card">
                        <CardHeader className="border-b border-zinc-800 pb-4">
                            <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide">
                                Load Status
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 space-y-3">
                            <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-sm">
                                <div className="flex items-center gap-3">
                                    <div className="w-2 h-2 rounded-full bg-yellow-500" />
                                    <span className="text-zinc-300">Pending</span>
                                </div>
                                <span className="text-white font-mono">{stats?.loads?.pending || 0}</span>
                            </div>
                            <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-sm">
                                <div className="flex items-center gap-3">
                                    <div className="w-2 h-2 rounded-full bg-orange-500" />
                                    <span className="text-zinc-300">Booked</span>
                                </div>
                                <span className="text-white font-mono">{stats?.loads?.booked || 0}</span>
                            </div>
                            <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-sm">
                                <div className="flex items-center gap-3">
                                    <div className="w-2 h-2 rounded-full bg-blue-500 live-indicator" />
                                    <span className="text-zinc-300">In Transit</span>
                                </div>
                                <span className="text-white font-mono">{stats?.loads?.in_transit || 0}</span>
                            </div>
                            <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-sm">
                                <div className="flex items-center gap-3">
                                    <div className="w-2 h-2 rounded-full bg-green-500" />
                                    <span className="text-zinc-300">Delivered</span>
                                </div>
                                <span className="text-white font-mono">{stats?.loads?.delivered || 0}</span>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Invoices Summary */}
                    <Card className="bg-zinc-900/50 border-zinc-800" data-testid="invoices-summary-card">
                        <CardHeader className="border-b border-zinc-800 pb-4">
                            <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide">
                                Invoices
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-zinc-400">Pending</span>
                                <span className="text-yellow-500 font-mono">{stats?.invoices?.pending || 0}</span>
                            </div>
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-zinc-400">Collected</span>
                                <span className="text-green-500 font-mono">{formatCurrency(stats?.revenue?.collected)}</span>
                            </div>
                            <Button 
                                variant="outline" 
                                className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 mt-2"
                                onClick={() => navigate('/financials')}
                                data-testid="view-financials-btn"
                            >
                                <DollarSign className="w-4 h-4 mr-2" />
                                View Financials
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Expense Breakdown (Last 30 Days) */}
                    <Card className="bg-zinc-900/50 border-zinc-800" data-testid="expense-breakdown-card">
                        <CardHeader className="border-b border-zinc-800 pb-4">
                            <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide">
                                Expense Breakdown
                            </CardTitle>
                            <p className="text-xs text-zinc-500 mt-1">Last 30 days</p>
                        </CardHeader>
                        <CardContent className="p-4">
                            {(!accountingData?.expense_breakdown_last_30_days || 
                              accountingData.expense_breakdown_last_30_days.length === 0) ? (
                                <p className="text-zinc-500 text-sm text-center py-4">
                                    No expenses recorded in last 30 days.
                                </p>
                            ) : (
                                <div className="space-y-3">
                                    {accountingData.expense_breakdown_last_30_days.map((expense, index) => (
                                        <div 
                                            key={expense.expense_type} 
                                            className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-sm"
                                            data-testid={`expense-row-${expense.expense_type}`}
                                        >
                                            <span className={`capitalize ${index === 0 ? 'text-white font-semibold' : 'text-zinc-300'}`}>
                                                {expense.expense_type.replace('_', ' ')}
                                            </span>
                                            <span className={`font-mono ${index === 0 ? 'text-red-400 font-semibold' : 'text-zinc-400'}`}>
                                                {formatCurrency(expense.total_amount)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Truck Revenue Performance (Testing Mode Only) */}
                    <TruckPerformancePanel systemMode={settings?.system_mode} />
                </div>
            </div>
        </div>
    );
}
